import { useState } from "react";
import {
  updateHarvestedPlots,
  updateUnHarvestedPlots,
} from "../../../utils/api.js";
import { isValidBackendDate } from "./harvestEntryHelpers.js";
import { UNSAVED_FILTER_LABEL } from "./harvestEntryConstants.js";

export function useHarvestEntrySave({
  rows,
  rowsRef,
  setRows,
  rowHasPendingChanges,
  showWeight,
  statusColFilterValue,
  orgId,
  siteId,
  cropId,
  clearRowSelection,
  messageApi,
  refreshHarvestRef,
  unharvestConcurrency,
  harvestConcurrency,
  harvestConfig,
  harvestConfigError,
}) {
  const [savingAll, setSavingAll] = useState(false);

  const pickPendingFields = (r) => ({
    pendingUnharvest: !!r?.pendingUnharvest,
    pendingHarvestDate:
      r?.pendingHarvestDate !== undefined ? r.pendingHarvestDate : null,
    pendingYield: r?.pendingYield !== undefined ? r.pendingYield : null,
    pendingWeight: r?.pendingWeight !== undefined ? r.pendingWeight : null,
    pendingTubWeight: r?.pendingTubWeight ?? null,
    pendingPloughedReasons: r?.pendingPloughedReasons ?? null,
    pendingPloughedNote: r?.pendingPloughedNote ?? null,
  });

  const snapshotPendingById = (rowsList, idSet) => {
    const map = new Map();
    for (const r of rowsList || []) {
      if (idSet.has(r.id)) map.set(r.id, pickPendingFields(r));
    }
    return map;
  };

  const runPool = async (items, concurrency, worker) => {
    const list = Array.isArray(items) ? items : [];
    const limit = Math.max(1, Number(concurrency || 1));

    let i = 0;
    const results = new Array(list.length);

    const runners = Array.from(
      { length: Math.min(limit, list.length) },
      async () => {
        while (i < list.length) {
          const idx = i++;
          try {
            await worker(list[idx], idx);
            results[idx] = { ok: true };
          } catch (e) {
            results[idx] = { ok: false, error: e };
          }
        }
      },
    );

    await Promise.all(runners);
    return results;
  };

  const handleSaveVisible = async () => {
    const toSave = (rowsRef.current ?? rows).filter(rowHasPendingChanges);

    if (!toSave.length) {
      messageApi.info("No changes to save.");
      return;
    }
    if (!orgId || !siteId || !cropId) {
      messageApi.error("Org/Site/Crop not selected.");
      return;
    }
    const toUnharvest = toSave.filter((r) => r.pendingUnharvest);
    const toHarvest = toSave.filter((r) => !r.pendingUnharvest);

    if (toHarvest.length > 0 && !harvestConfig?.tub_weight) {
      messageApi.error(
        harvestConfigError
          ? "Harvest config failed to load. Please refresh the page."
          : "Harvest config is still loading. Please try again.",
      );
      return;
    }

    setSavingAll(true);

    try {
      const [unharvestRes, harvestRes] = await Promise.all([
        runPool(toUnharvest, unharvestConcurrency, async (row) => {
          await updateUnHarvestedPlots(
            orgId,
            siteId,
            cropId,
            row.greenhouse_id,
            row.plot_id,
            row.greenhouse_name,
            row.plot_name,
          );
        }),

        runPool(toHarvest, harvestConcurrency, async (row) => {
          const finalDate = row.pendingHarvestDate || row.harvest_date;
          if (!isValidBackendDate(finalDate)) {
            throw new Error("Invalid harvest date");
          }

          const finalYield =
            row.pendingYield !== null && row.pendingYield !== undefined
              ? row.pendingYield
              : (row.yield ?? 0);

          const defaultTubWeight = harvestConfig?.tub_weight?.default ?? 0;
          const finalTubWeight =
            row.pendingTubWeight !== null && row.pendingTubWeight !== undefined
              ? row.pendingTubWeight
              : (row.weight_per_tub ?? defaultTubWeight);

          if (
            harvestConfig?.tub_weight &&
            (finalTubWeight < harvestConfig.tub_weight.min ||
              finalTubWeight > harvestConfig.tub_weight.max)
          ) {
            throw new Error(
              `Tub weight ${finalTubWeight} is outside the configured range [${harvestConfig.tub_weight.min}, ${harvestConfig.tub_weight.max}]`,
            );
          }

          const finalTubWeightSource =
            row.pendingTubWeight !== null && row.pendingTubWeight !== undefined
              ? "MANUAL"
              : (row.weight_per_tub_source ?? "SITE_DEFAULT");

          const isDiscarded = finalYield === -1;

          const finalTotalWeight = isDiscarded ? 0 : finalYield * finalTubWeight;

          const effectiveReasonIds = isDiscarded
            ? (row.pendingPloughedReasons !== null &&
              row.pendingPloughedReasons !== undefined
                ? row.pendingPloughedReasons
                : (row.ploughed_under_reasons ?? []).map((r) => r.id))
            : [];

          const finalPloughedNote = isDiscarded
            ? (row.pendingPloughedNote !== null &&
              row.pendingPloughedNote !== undefined
                ? row.pendingPloughedNote
                : (row.ploughed_under_note ?? null))
            : null;

          await updateHarvestedPlots(
            orgId,
            siteId,
            cropId,
            row.greenhouse_id,
            row.plot_id,
            finalDate,
            finalYield,
            finalTotalWeight,
            null,
            null,
            finalTubWeight,
            finalTubWeightSource,
            effectiveReasonIds,
            finalPloughedNote,
          );
        }),
      ]);

      const okUnharvest = toUnharvest.filter(
        (_, idx) => unharvestRes?.[idx]?.ok,
      );
      const failUnharvest = toUnharvest.filter(
        (_, idx) => !unharvestRes?.[idx]?.ok,
      );

      const okHarvest = toHarvest.filter((_, idx) => harvestRes?.[idx]?.ok);
      const failHarvest = toHarvest.filter((_, idx) => !harvestRes?.[idx]?.ok);

      const okRows = [...okUnharvest, ...okHarvest];
      const failedRows = [...failUnharvest, ...failHarvest];

      const okIds = new Set(okRows.map((r) => r.id));
      const failedIds = new Set(failedRows.map((r) => r.id));

      const okCount = okRows.length;
      const failedCount = failedRows.length;

      // --- snapshot failed pending BEFORE refresh/rebuild can touch rows ---
      const failedPendingSnapshot = snapshotPendingById(
        rowsRef.current,
        failedIds,
      );

      // Clear pending ONLY for successful rows
      if (okCount > 0) {
        setRows((prev) =>
          prev.map((r) => {
            if (!okIds.has(r.id)) return r;
            return {
              ...r,
              pendingUnharvest: false,
              pendingHarvestDate: null,
              pendingYield: null,
              pendingWeight: null,
              pendingTubWeight: null,
              pendingPloughedReasons: null,
              pendingPloughedNote: null,
            };
          }),
        );
      }

      // Messaging
      if (okCount === 0 && failedCount > 0) {
        messageApi.error(
          `Save failed. ${failedCount} row(s) are still in (Unsaved). Please save again.`,
        );
        return;
      }

      if (failedCount > 0) {
        messageApi.warning(
          `Saved ${okCount} row(s). ${failedCount} row(s) failed and are still in (Unsaved). Please save again.`,
        );
      } else {
        messageApi.success("Saved successfully.");
      }
      if (okCount > 0) {
        clearRowSelection();
        const nextLabelForSavedRow = (row) => {
          if (row.pendingUnharvest) return "Planted";
          const isDiscard =
            row.pendingYield === -1 || (showWeight && row.pendingWeight === -1);
          return isDiscard ? "Discarded" : "Harvested";
        };

        const visibleUnderStatusFilter = (nextLabel) => {
          if (statusColFilterValue === UNSAVED_FILTER_LABEL) return false;
          if (!statusColFilterValue) return true;
          return nextLabel === statusColFilterValue;
        };

        let movedOutCount = 0;
        for (const r of okRows) {
          const nextLabel = nextLabelForSavedRow(r);
          if (!visibleUnderStatusFilter(nextLabel)) movedOutCount += 1;
        }

        if (movedOutCount > 0) {
          const filterName =
            statusColFilterValue === UNSAVED_FILTER_LABEL
              ? "Unsaved"
              : statusColFilterValue || "All";
          messageApi.info(
            `Heads up: ${movedOutCount} saved row(s) are hidden because Status filter is set to ${filterName}.`,
          );
        }
      }

      // Refresh ONLY successful subset
      if (okCount > 0) {
        const greenhouseIds = Array.from(
          new Set(okRows.map((r) => r.greenhouse_id)),
        );
        const plotIds = Array.from(new Set(okRows.map((r) => r.plot_id)));

        try {
          await refreshHarvestRef.current?.({ greenhouseIds, plotIds });
        } catch (e) {
          console.error("Subset refresh failed:", e);
          messageApi.warning(
            "Saved successfully, but refresh failed. Data may look stale until next refresh.",
          );
        }

        // Re-apply failed pending after refresh rebuild
        if (failedPendingSnapshot.size > 0) {
          setRows((prev) =>
            prev.map((r) => {
              const pending = failedPendingSnapshot.get(r.id);
              if (!pending) return r;
              return { ...r, ...pending };
            }),
          );
        }
      }
    } catch (err) {
      console.error("Error saving changes:", err);
      messageApi.error("Error saving changes.");
    } finally {
      setSavingAll(false);
    }
  };

  return {
    savingAll,
    handleSaveVisible,
  };
}
